import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';
import { motion } from 'framer-motion';

// Wir importieren ChangeVibeModal hier nicht mehr, es wird von der MapPage gesteuert.

const ICONS = { myVibes: '🎮', joined: '🕹️', pending: '❓' };

const MyEventsModal = ({ onClose, onEventClick, onOpenChangeVibe }) => {
    const { user, api } = useAuth();
    const { t, i18n } = useTranslation();
    const { deleteEvent, leaveEvent, updateParticipantStatus } = useEvents();

    const [activeTab, setActiveTab] = useState('myVibes');
    const [myVibes, setMyVibes] = useState([]);
    const [joinedVibes, setJoinedVibes] = useState([]);
    const [pendingVibes, setPendingVibes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const socketRef = useRef(null);

    const fetchMyPersonalEvents = useCallback(async () => {
        if (!api || !user) return;
        setIsLoading(true);
        try {
            const res = await api.get('/api/events/my-events');
            if (res.data) {
                setMyVibes(res.data.filter(e => e.user_id === user.id));
                setJoinedVibes(res.data.filter(e => e.user_id !== user.id && e.participants.some(p => p.user_id === user.id && p.status === 'accepted')));
                setPendingVibes(res.data.filter(e => e.user_id !== user.id && e.participants.some(p => p.user_id === user.id && p.status === 'pending')));
            }
        } catch (e) { console.error("Could not fetch personal events", e); } 
        finally { setIsLoading(false); }
    }, [api, user]);
    
    useEffect(() => {
        fetchMyPersonalEvents();
        const setupSocket = async () => {
            if (socketRef.current) socketRef.current.disconnect();
            try {
                const res = await api.get('/api/auth/check');
                const token = res.data.accessKey;
                if (!token) return;
                socketRef.current = io(window.location.origin, { auth: { token } });
                const socket = socketRef.current;
                const handleUpdate = () => fetchMyPersonalEvents();
                socket.on('eventUpdated', handleUpdate);
                socket.on('newEvent', handleUpdate);
                socket.on('eventDeleted', handleUpdate);
            } catch (e) { console.error("Modal socket setup failed", e); }
        };
        setupSocket();
        return () => { if (socketRef.current) { socketRef.current.disconnect(); } };
    // eslint-disable-next-line
    }, [api]);

    const handleDeleteWrapper = (eventId) => {
        if (window.confirm(t('delete_vibe_confirm'))) {
            deleteEvent(eventId);
        }
    };
    const handleLeaveWrapper = (eventId) => {
        if (window.confirm(t('leave_vibe_confirm'))) {
            leaveEvent(eventId);
        }
    };
    const handleParticipantStatusWrapper = (eventId, userId, status) => {
        updateParticipantStatus(eventId, userId, status);
    };
    
    if (!user) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <motion.div
                className="modal-content floating-modal"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 20 }}
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', height: '80vh' }}
            >
                <div style={{flexShrink: 0}}>
                    <div style={{display:'flex', alignItems:'center', gap: '20px', padding: '20px', background: 'rgba(0,0,0,0.2)'}}>
                        <img src={user.selfie_path ? `/${user.selfie_path}`: ''} alt="me" style={{width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: `3px solid #000`}} />
                        <div>
                            <h2 style={{margin:0, fontFamily: 'var(--font-display)', fontSize: '2em'}}>{user.nickname}</h2>
                            <button onClick={onOpenChangeVibe} style={{padding: '8px 12px', background: 'transparent', border: '2px solid var(--color-text-hellblau)'}}>
                                {t('change_favorite_vibe_button')}
                            </button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', borderBottom: '3px solid #000', borderTop: '3px solid #000' }}>
                        <TabButton id="myVibes" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.myVibes} {t('myVibes_tab')} ({myVibes.length})</TabButton>
                        <TabButton id="joined" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.joined} {t('joined_tab')} ({joinedVibes.length})</TabButton>
                        <TabButton id="pending" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.pending} {t('pending_tab')} ({pendingVibes.length})</TabButton>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: 'rgba(0,0,0,0.1)' }}>
                    {isLoading ? <p style={{textAlign: 'center'}}>{t('loading')}...</p> : (
                        <>
                            {activeTab === 'myVibes' && <MyVibesTab events={myVibes} onEventClick={onEventClick} handleDelete={handleDeleteWrapper} handleParticipantStatus={handleParticipantStatusWrapper} t={t} i18n={i18n} />}
                            {activeTab === 'joined' && <JoinedTab events={joinedVibes} onEventClick={onEventClick} handleLeave={handleLeaveWrapper} t={t} i18n={i18n} />}
                            {activeTab === 'pending' && <PendingTab events={pendingVibes} onEventClick={onEventClick} handleLeave={handleLeaveWrapper} t={t} i18n={i18n} />}
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

const TabButton = ({ id, activeTab, setActiveTab, children }) => ( <button style={{ flex: 1, padding: '15px', background: 'transparent', border: 'none', borderBottom: activeTab === id ? '3px solid var(--color-text-hellblau)' : '3px solid transparent' }} onClick={() => setActiveTab(id)}> {children} </button> );

const MyVibesTab = ({ events, onEventClick, handleDelete, handleParticipantStatus, t, i18n }) => (
    <div style={{display:'flex', flexDirection: 'column', gap: '10px'}}>
        {events.length > 0 ? events.map(event => {
            const requests = event.participants.filter(p => p.status === 'pending');
            const acceptedCount = event.participants.filter(p => p.status === 'accepted').length - 1;
            return (
                <div key={event.id} style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '12px', border: '2px solid #000'}}>
                    {/* Erste Zeile: Event-Info + Stats + Delete */}
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: requests.length > 0 ? '10px' : '0'}}>
                        <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                        <div style={{flex: 1, minWidth: 0}}>
                            <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                            <div style={{fontSize: '0.75em', opacity: 0.8}}>
                                {new Date(event.event_date).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                        <div style={{display: 'flex', gap: '8px', fontSize: '0.8em', flexShrink: 0}}>
                            <span>✔️{acceptedCount}</span>
                            <span>❓{requests.length}</span>
                        </div>
                        <button onClick={() => handleDelete(event.id)} style={{padding: '4px 8px', fontSize: '0.75em', background: '#ff4d4d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>🗑️</button>
                    </div>
                    {/* Request-Liste */}
                    {requests.map(p => (
                        <div key={p.user_id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', marginTop: '6px'}}>
                            <img src={p.selfie_path ? `/${p.selfie_path.replace('.webp', '-thumb.webp')}` : ''} alt={p.nickname} style={{width: 30, height: 30, borderRadius: '50%', objectFit: 'cover'}}/>
                            <span style={{flex: 1, fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.nickname}</span>
                            <button onClick={()=>handleParticipantStatus(event.id,p.user_id,'accepted')} style={{padding: '4px 8px', fontSize: '0.75em', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>✓</button>
                            <button onClick={()=>handleParticipantStatus(event.id,p.user_id,'rejected')} style={{padding: '4px 8px', fontSize: '0.75em', background: '#ff4d4d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>✗</button>
                        </div>
                    ))}
                </div>
            );
        }) : <p style={{textAlign:'center'}}>{t('no_created_vibes')}</p>}
    </div>
);

const JoinedTab = ({ events, onEventClick, handleLeave, t, i18n }) => (
    <div style={{display:'flex', flexDirection: 'column', gap: '8px'}}>
        {events.length > 0 ? events.map(event => (
            <div key={event.id} style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '12px', border: '2px solid #000'}}>
                <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                <img src={event.creator_selfie ? `/${event.creator_selfie.replace('.webp', '-thumb.webp')}` : ''} alt={event.creator_nickname} style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}/>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                    <div style={{fontSize: '0.75em', opacity: 0.8}}>
                        w/ {event.creator_nickname} @ {new Date(event.event_date).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
                <button onClick={() => handleLeave(event.id)} style={{padding: '4px 10px', fontSize: '0.75em', background: '#ff4d4d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>Leave</button>
            </div>
        )) : <p style={{textAlign:'center'}}>{t('no_joined_vibes')}</p>}
    </div>
);

const PendingTab = ({ events, onEventClick, handleLeave, t, i18n }) => (
    <div style={{display:'flex', flexDirection: 'column', gap: '8px'}}>
        {events.length > 0 ? events.map(event => (
            <div key={event.id} style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '12px', border: '2px solid #000', opacity: 0.7}}>
                <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                <img src={event.creator_selfie ? `/${event.creator_selfie.replace('.webp', '-thumb.webp')}` : ''} alt={event.creator_nickname} style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}/>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                    <div style={{fontSize: '0.75em', opacity: 0.8}}>
                        w/ {event.creator_nickname} @ {new Date(event.event_date).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
                <button onClick={() => handleLeave(event.id)} style={{padding: '4px 10px', fontSize: '0.75em', background: '#ff4d4d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>Cancel</button>
            </div>
        )) : <p style={{textAlign:'center'}}>{t('no_pending_vibes')}</p>}
    </div>
);

export default MyEventsModal;
